import { useState } from 'react';
import { Node } from 'reactflow';
import { displaySn } from '../../utils/deviceDisplay';
import { architectureApi } from '../../api/architecture';
import { useI18n } from '../../i18n';
import type { TranslationKey } from '../../i18n/translations';
import { displayDefaultName } from '../../i18n/defaultNames';

type TFn = (key: TranslationKey, vars?: Record<string, string | number>) => string;
type TpFn = (base: string, n: number) => string;

/**
 * Libellé affiché d'une marque de tableau. La valeur enregistrée (data.marque) reste
 * en français ; seule l'affichage est traduit.
 */
export function tableauBrandLabel(marque: string | undefined, t: TFn): string {
  if (marque === 'Modulaire') return t('arch2_brand_modular');
  if (marque === 'Étanche IP65') return t('arch2_brand_waterproof');
  return marque || '';
}

/**
 * Nom affiché d'un tableau. Les noms par défaut (« Tableau 2 rangées »…) sont enregistrés
 * en base comme nom de zone et relus par préfixe (« Tableau… ») : la valeur stockée reste
 * en français, on ne traduit que l'affichage des noms par défaut connus.
 */
export function tableauDisplayName(label: string | undefined, t: TFn, tp: TpFn): string {
  if (!label) return t('arch2_electrical_panel');
  return displayDefaultName(label, t, tp);
}

const CIRCUIT_KEYS: TranslationKey[][] = [
  ['arch2_circ_light', 'arch2_circ_sockets', 'arch2_circ_kitchen', 'arch2_circ_bath'],
  ['arch2_circ_heating', 'arch2_circ_vmc', 'arch2_circ_fridge', 'arch2_circ_washer'],
  ['arch2_circ_oven', 'arch2_circ_hob', 'arch2_circ_dryer', 'arch2_circ_garage'],
  ['arch2_circ_outdoor', 'arch2_circ_garden', 'arch2_circ_pool', 'arch2_circ_spare'],
];

interface TableauElectriqueModalProps {
  editingTableauNode: Node | null;
  setEditingTableauNode: (node: Node | null) => void;
  devices: any[];
  addWattelemToPanel: (device: any, tableauId: string) => void;
  removeWattelemFromPanel: (deviceSn: string, tableauId: string) => void;
}

export const TableauElectriqueModal = ({
  editingTableauNode,
  setEditingTableauNode,
  devices,
  addWattelemToPanel,
  removeWattelemFromPanel,
}: TableauElectriqueModalProps) => {
  const { t, tp } = useI18n();
  // ── Ajout par numéro de série (hooks avant tout return conditionnel — Rules of Hooks) ──
  const [snInput, setSnInput] = useState('');
  const [snError, setSnError] = useState<string | null>(null);
  const [snChecking, setSnChecking] = useState(false);

  if (!editingTableauNode) return null;

  // Données du tableau
  const rangees = editingTableauNode.data?.rangees || 3;
  const totalModules = editingTableauNode.data?.modules || 39;
  const marque = editingTableauNode.data?.marque
    ? tableauBrandLabel(editingTableauNode.data.marque, t)
    : t('arch2_electrical_panel');

  // WattelEM installés dans ce tableau (depuis wattelemList du node)
  const installedWattelem = editingTableauNode.data?.wattelemList || [];

  // Filtre : garder uniquement les WattelEM (toute chaîne contenant "em" insensible à la casse)
  // Matches: ZCE-EM-*, wattelEM, wattel_EM_*, WattelEM_*, et tout device dont
  // un champ (type/modèle/etc.) contient "em".
  // Ne matche PAS: ZCE-WE-*, ZCE-GE-*, prise_telerupteur_*, etc.
  const isWattelEMDevice = (d: any) => {
    if (!d) return false;
    for (const key of Object.keys(d)) {
      const v = d[key];
      if (typeof v === 'string' && v.toLowerCase().includes('em')) return true;
    }
    return false;
  };

  // ── Ajout par numéro de série (au lieu de lister tous les WattelEM) ──
  const handleAddBySn = async () => {
    const sn = snInput.trim();
    if (!sn) return;
    setSnError(null);

    // Déjà installé dans ce tableau ?
    if (installedWattelem.some((w: any) => w.sn === sn)) {
      setSnError(t('arch2_wem_already_installed'));
      return;
    }

    // Recherche du device en base (insensible à la casse)
    const device = devices.find(
      d => String(d.deviceSn || '').toLowerCase() === sn.toLowerCase()
    );

    if (!device) {
      setSnError(t('arch2_no_device_sn'));
      return;
    }

    if (!isWattelEMDevice(device)) {
      setSnError(t('arch2_not_wattelem'));
      return;
    }

    // Exclusivité globale : refuse l'ajout si le device est déjà utilisé dans
    // une autre installation (vérifié côté backend AVANT d'ajouter au canvas).
    setSnChecking(true);
    try {
      const excludeId = Number(sessionStorage.getItem('currentInstallationId')) || undefined;
      const resp = await architectureApi.checkDeviceAvailable(device.deviceSn, excludeId);
      if (!resp?.data?.available) {
        setSnError(t('arch2_device_in_use'));
        return;
      }
    } catch {
      setSnError(t('arch2_device_check_failed'));
      return;
    } finally {
      setSnChecking(false);
    }

    const isOnline = device.online;
    const power = Number(device.powerKw) || 0;
    const deviceName = device.name || displaySn(device.deviceName) || displaySn(device.deviceSn);

    addWattelemToPanel(
      {
        sn: device.deviceSn,
        name: deviceName,
        power,
        status: isOnline ? 'online' : 'offline',
      },
      editingTableauNode.id
    );

    setSnInput('');
    setSnError(null);
  };

  // Circuits standards selon le nombre de rangées
  const getCircuitsForRow = (rowIndex: number) => {
    return CIRCUIT_KEYS[rowIndex % CIRCUIT_KEYS.length].map(k => t(k));
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-auto animate-fade-in-scale"
      onClick={(e) => {
        if (e.target === e.currentTarget) setEditingTableauNode(null);
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto transform transition-all duration-300" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 text-white p-4 flex justify-between items-center sticky top-0 z-10 shadow-lg">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <span className="animate-electric-spark">⚡</span> {tableauDisplayName(editingTableauNode.data?.label, t, tp)}
            </h2>
            <p className="text-sm text-gray-300">{marque} • {tp('arch2_rows', rangees)} • {tp('arch3_modules_count', totalModules)}</p>
          </div>
          <button 
            onClick={() => setEditingTableauNode(null)}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-bold transition-all duration-200 hover:scale-105 hover:shadow-lg"
          >
            {t('arch2_close_x')}
          </button>
        </div>

        {/* Contenu du tableau */}
        <div className="p-6">
          {/* Coffret électrique visuel */}
          <div className="bg-gradient-to-b from-gray-100 to-gray-200 border-8 border-gray-400 rounded-xl p-4 mb-6 shadow-inner">
            
            {/* Rangée Protection (toujours présente) */}
            <div className="mb-4">
              <div className="text-xs text-gray-500 mb-2 font-bold tracking-wide">{t('arch2_main_protection')}</div>
              <div className="bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 h-2 rounded mb-2 animate-shimmer"></div>
              <div className="flex gap-2 flex-wrap">
                {/* Disjoncteur principal */}
                <div className="w-12 h-20 bg-gray-800 rounded flex flex-col items-center justify-between p-1 text-white text-[8px] shadow-md hover:scale-105 transition-transform cursor-pointer">
                  <span>{t('arch3_main_breaker')}</span>
                  <div className="w-6 h-3 bg-green-500 rounded"></div>
                  <span>63A</span>
                </div>
                {/* Différentiels - nombre selon les rangées */}
                {[...Array(Math.min(rangees, 3))].map((_, i) => (
                  <div key={i} className="w-10 h-20 bg-gray-200 border border-gray-400 rounded flex flex-col items-center justify-between p-1 text-[7px]">
                    <div className="w-5 h-2 bg-green-500 rounded"></div>
                    <div className="w-3 h-2 bg-green-400 rounded"></div>
                    <span className="text-center">{t('arch2_rcd_30ma')}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Rangée WattelEM (toujours présente) */}
            <div className="mb-4">
              <div className="text-xs text-gray-500 mb-2 font-bold">{t('arch2_wattelem_installed_n', { n: installedWattelem.length })}</div>
              <div className="bg-cyan-400 h-2 rounded mb-2"></div>
              <div className="flex gap-3 flex-wrap min-h-[100px] bg-gray-800 p-3 rounded border-2 border-cyan-500/30">
                {/* WattelEM installés */}
                {installedWattelem.map((wem: any, index: number) => {
                  const device = devices.find(d => d.deviceSn === wem.sn);
                  const isOnline = wem.status === 'online' || device?.online;
                  const power = Number(wem.power) || Number(device?.powerKw) || 0;
                  const displayName = wem.name || device?.name || displaySn(device?.deviceName) || displaySn(wem.sn);
                  
                  return (
                    <div 
                      key={wem.sn || index}
                      className={`relative w-20 h-28 rounded-lg border-2 ${isOnline ? 'border-cyan-500 bg-gray-900' : 'border-gray-400 bg-gray-700'} flex flex-col items-center p-1 shadow-lg group`}
                    >
                      {/* Bouton supprimer */}
                      <button
                        onClick={() => removeWattelemFromPanel(wem.sn, editingTableauNode.id)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity shadow-lg z-10"
                        title={t('arch2_remove_wattelem')}
                      >
                        ✕
                      </button>
                      {/* Écran */}
                      <div className={`w-16 h-12 ${isOnline ? 'bg-[#0a2530]' : 'bg-gray-800'} rounded text-center p-1`}>
                        <div className={`text-[11px] font-mono font-bold ${isOnline ? 'text-cyan-300' : 'text-gray-500'}`}>
                          230V ↝
                        </div>
                        <div className={`text-[10px] font-mono ${isOnline ? 'text-green-400' : 'text-gray-500'}`}>
                          {(power * 4.35).toFixed(1)}A
                        </div>
                        <div className={`text-[9px] font-mono ${isOnline ? 'text-white' : 'text-gray-500'}`}>
                          {power.toFixed(2)} kW
                        </div>
                      </div>
                      {/* LEDs */}
                      <div className="flex gap-1 mt-1">
                        <div className={`w-4 h-4 rounded-full ${isOnline ? 'bg-orange-500' : 'bg-gray-500'}`}></div>
                        <div className={`w-4 h-4 rounded-full ${isOnline ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                      </div>
                      <div className="text-[7px] text-white mt-1 truncate w-full text-center" title={displayName}>
                        {displayName?.slice(-10) || t('arch3_not_available')}
                      </div>
                    </div>
                  );
                })}
                
                {/* Message si vide */}
                {installedWattelem.length === 0 && (
                  <div className="flex-1 flex items-center justify-center text-gray-400">
                    {t('arch2_no_wattelem_installed')}
                  </div>
                )}
              </div>
            </div>

            {/* Rangées Circuits (dynamiques selon rangees - 1, car protection et wattelem comptent) */}
            {[...Array(Math.max(0, rangees - 1))].map((_, rowIdx) => (
              <div key={`circuits-${rowIdx}`} className={rowIdx < rangees - 2 ? 'mb-4' : ''}>
                <div className="text-xs text-gray-500 mb-2 font-bold">{t('arch2_circuits_n', { n: rowIdx + 1 })}</div>
                <div className="bg-yellow-400 h-2 rounded mb-2"></div>
                <div className="flex gap-2 flex-wrap">
                  {getCircuitsForRow(rowIdx).map((label, i) => (
                    <div key={i} className="w-10 h-16 bg-gray-200 border border-gray-400 rounded flex flex-col items-center justify-between p-1 text-[7px]">
                      <div className="w-5 h-2 bg-green-500 rounded"></div>
                      <div className="w-3 h-2 bg-green-400 rounded"></div>
                      <span className="text-center">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Ajout d'un WattelEM par numéro de série */}
          <div className="bg-gray-800 rounded-xl p-4">
            <h3 className="text-white font-bold mb-1 flex items-center gap-2">
              {t('arch2_add_wattelem')}
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              {t('arch2_add_wattelem_hint')}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-start">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-300 mb-1.5">{t('arch2_serial_number')}</label>
                <input
                  type="text"
                  value={snInput}
                  onChange={(e) => { setSnInput(e.target.value); if (snError) setSnError(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddBySn(); }}
                  placeholder={t('arch2_example', { v: 'ZCE-EM-e2e-1779...' })}
                  className="w-full px-4 py-2.5 rounded-xl bg-gray-900 border-2 border-gray-600 text-sm text-cyan-100 font-mono placeholder-gray-600 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30 transition-all"
                />
                {snError && (
                  <p className="mt-2 text-xs text-red-400 flex items-center gap-1">
                    <span>⚠️</span> {snError}
                  </p>
                )}
              </div>
              <button
                onClick={handleAddBySn}
                disabled={!snInput.trim() || snChecking}
                className="sm:mt-[26px] px-5 py-2.5 text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-500 rounded-xl shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                {snChecking ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                )}
                {t('arch2_add')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
